/**
 * locale-detector.ts
 *
 * Deterministic BCP 47 subtag resolver and browser locale detector.
 * Normalizes regional variants (zh-CN, fr-CA, es-MX) to supported platform tags.
 */

import { type Bcp47LocaleTag, SUPPORTED_LOCALES } from './locale-contracts.js';

const REGIONAL_FALLBACK_MAP: Record<string, Bcp47LocaleTag> = {
  // Chinese variants
  'zh-cn': 'zh-Hans',
  'zh-sg': 'zh-Hans',
  'zh-hans': 'zh-Hans',
  'zh-tw': 'zh-Hans',
  'zh-hk': 'zh-Hans',
  'zh': 'zh-Hans',

  // Spanish variants
  'es-es': 'es',
  'es-mx': 'es',
  'es-ar': 'es',
  'es-co': 'es',
  'es-cl': 'es',
  'es-419': 'es',
  'es': 'es',

  // French variants
  'fr-fr': 'fr',
  'fr-ca': 'fr',
  'fr-be': 'fr',
  'fr-ch': 'fr',
  'fr': 'fr',

  // German variants
  'de-de': 'de',
  'de-at': 'de',
  'de-ch': 'de',
  'de': 'de',

  // Arabic variants
  'ar-sa': 'ar',
  'ar-eg': 'ar',
  'ar-ae': 'ar',
  'ar-ma': 'ar',
  'ar': 'ar',

  // English variants
  'en-us': 'en',
  'en-gb': 'en',
  'en-ca': 'en',
  'en-au': 'en',
  'en': 'en',
};

export function normalizeBcp47Locale(tag: string | null | undefined): Bcp47LocaleTag {
  if (!tag) return 'en';
  const clean = tag.trim().toLowerCase();

  // 1. Direct match in regional fallback map
  if (REGIONAL_FALLBACK_MAP[clean]) {
    return REGIONAL_FALLBACK_MAP[clean];
  }

  // 2. Primary language subtag match (e.g. "fr-XX" -> "fr")
  const primary = clean.split('-')[0];
  if (REGIONAL_FALLBACK_MAP[primary]) {
    return REGIONAL_FALLBACK_MAP[primary];
  }

  // 3. Fallback to English
  return 'en';
}

export function detectBrowserLocale(): Bcp47LocaleTag {
  try {
    if (typeof navigator !== 'undefined') {
      if (navigator.languages && navigator.languages.length > 0) {
        for (const lang of navigator.languages) {
          const matched = normalizeBcp47Locale(lang);
          if (SUPPORTED_LOCALES[matched]) return matched;
        }
      }
      if (navigator.language) {
        return normalizeBcp47Locale(navigator.language);
      }
    }
  } catch {
    // Ignore in non-browser environments
  }
  return 'en';
}
